import { ErrorTypes, createErrorWithType } from '../../utils/errorHandling';
import { getBrowserFingerprint, detectIncognito } from '../browserFingerprint';

/**
 * TrialService - Centralized service for handling trial access
 * Responsible for trial registration, status checking, and test recording
 */
class TrialService {
  constructor() {
    this.STORAGE_KEY = 'trial_status';
    this.lastAttemptTime = 0;
    this.cachedStatus = null;
    this.retryDelay = 30000; // 30 seconds between retries
    this.isLocalDevelopment = window.location.hostname === 'localhost';
  }

  /**
   * Get the current trial status for the user
   * @param {boolean} forceRefresh - Whether to force a refresh of the status
   * @returns {Promise<Object>} The trial status
   */
  async getUserStatus(forceRefresh = false) {
    try {
      // Check if we have a cached status and if we're within the retry delay period
      const now = Date.now();
      if (!forceRefresh && this.cachedStatus && (now - this.lastAttemptTime < this.retryDelay)) {
        console.log('[TRIAL] Using cached status:', this.cachedStatus);
        return this.cachedStatus;
      }
      
      // Update last attempt time
      this.lastAttemptTime = now;
      
      // Check if a test has been taken in this session
      const testTakenToday = sessionStorage.getItem('test_taken_today') === 'true';
      if (testTakenToday) {
        console.log('[TRIAL] Test already taken in this session');
        // For session limited users, calculate next test time based on current time + 24 hours
        const nextTestTime = new Date(Date.now() + 24 * 60 * 60 * 1000);
        
        this.cachedStatus = {
          type: 'trial',
          testsAvailable: 0,
          nextTestTime: nextTestTime.toISOString(),
          sessionLimited: true
        };
        return this.cachedStatus;
      }
      
      // Check if we're in incognito mode
      const isIncognito = await this.checkIncognitoMode();
      
      // For local development without API, provide a fallback
      if (this.isLocalDevelopment) {
        return this.getLocalDevelopmentStatus();
      }
      
      // Get browser fingerprint
      const fingerprint = await getBrowserFingerprint();
      const sessionId = this.getOrCreateSessionId();

      // Try to get trial access
      console.log('[TRIAL] Registering for trial with fingerprint:', 
        fingerprint ? (fingerprint.substring(0, 10) + '...') : 'null or undefined');
      
      // Prepare headers
      const headers = this.prepareHeaders(fingerprint, sessionId, isIncognito);
      
      const response = await fetch('/api/trial/register', {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error('Error checking trial status');
      }

      const data = await response.json();
      
      if (data.success) {
        const expiresAt = new Date(data.trial.expiresAt);
        const now = new Date();
        
        // Calculate tests available (1 per day)
        const testsAvailable = expiresAt > now && !data.trial.ipLimited ?
          (data.trial.testsAvailable ?? 1) : 0;
        
        // Always calculate next test time based on the last_test_date + 24 hours
        // This is when the next test will be available (1 test per day)
        let nextTestTime = null;
        
        if (data.trial.last_test_date) {
          const lastTestDate = new Date(data.trial.last_test_date);
          // Next test available 24 hours after the last test
          const nextAvailableTime = new Date(lastTestDate.getTime() + 24 * 60 * 60 * 1000);
          nextTestTime = nextAvailableTime.toISOString();
        }
        
        // If no last_test_date is available, use current time + 24 hours
        if (!nextTestTime && testsAvailable === 0) {
          nextTestTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        }
        
        this.cachedStatus = {
          type: 'trial',
          testsAvailable,
          nextTestTime: testsAvailable === 0 ? nextTestTime : null,
          ipLimited: data.trial.ipLimited || false
        };
        
        return this.cachedStatus;
      }

      this.cachedStatus = {
        type: 'trial',
        testsAvailable: 0,
        nextTestTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };
      
      return this.cachedStatus;

    } catch (error) {
      console.error('[TRIAL] Error getting trial status:', error);
      
      // Cache the error status to prevent continuous retries
      this.cachedStatus = {
        type: 'trial',
        testsAvailable: 0,
        error: error.message
      };
      
      return this.cachedStatus;
    }
  }

  /**
   * Record that a test has been taken
   * @returns {Promise<boolean>} True if recording was successful
   */
  async recordTestTaken() {
    // Get current status with force refresh to ensure it's up-to-date
    const status = await this.getUserStatus(true);
    
    // If we're in local development mode, update the local storage
    if (this.isLocalDevelopment && status.isLocalSimulation) {
      return this.recordLocalTestTaken();
    }
    
    // For production, call the API to record the test taken
    try {
      const fingerprint = await getBrowserFingerprint();
      const sessionId = this.getOrCreateSessionId();
      const isIncognito = await this.checkIncognitoMode();
      
      console.log('[TRIAL] Recording test taken with fingerprint:', 
        fingerprint ? (fingerprint.substring(0, 10) + '...') : 'null or undefined');
      
      // Prepare headers
      const headers = this.prepareHeaders(fingerprint, sessionId, isIncognito);
      headers['X-Test-Taken'] = 'true';
      
      const response = await fetch('/api/trial/register', {
        method: 'POST',
        headers: headers,
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        console.error('[TRIAL] Failed to record test taken:', response.statusText);
        return false;
      }
      
      // Update cached status to reflect test taken
      if (this.cachedStatus) {
        this.cachedStatus.testsAvailable = 0;
        
        // Calculate next test time based on current time + 24 hours
        // This is when the next test will be available (1 test per day)
        const nextTestTime = new Date(Date.now() + 24 * 60 * 60 * 1000);
        this.cachedStatus.nextTestTime = nextTestTime.toISOString();
      }
      
      // Also store a flag in sessionStorage to prevent multiple tests in the same session
      sessionStorage.setItem('test_taken_today', 'true');
      
      // Force invalidate the cache to ensure next status check gets fresh data
      this.lastAttemptTime = 0;
      this.cachedStatus = null;
      
      return true;
    } catch (error) {
      console.error('[TRIAL] Error recording test taken:', error);
      return false;
    }
  }

  /**
   * Format the time until the next test is available
   * @param {string} nextTestTime - ISO string of next test time
   * @returns {string} Formatted time string
   */
  formatTimeUntilNextTest(nextTestTime) {
    if (!nextTestTime) return 'No disponible';
    
    const now = new Date();
    const next = new Date(nextTestTime);
    const diff = next - now;
    
    if (diff <= 0) return 'Disponible ahora';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Check if the browser is in incognito mode
   * @returns {Promise<boolean>} True if in incognito mode
   */
  async checkIncognitoMode() {
    try {
      // Multiple incognito detection methods for better accuracy
      let isIncognito = false;
      let incognitoDetectionMethods = [];
      
      // Method 1: Use the detectIncognito function
      try {
        const result = await detectIncognito();
        isIncognito = isIncognito || result;
        incognitoDetectionMethods.push({ method: 'detectIncognito', result });
      } catch (e) {
        console.error('[TRIAL] Error in detectIncognito method:', e);
      }
      
      // Method 2: Check localStorage availability
      try {
        localStorage.setItem('incognito_test', '1');
        localStorage.removeItem('incognito_test');
        incognitoDetectionMethods.push({ method: 'localStorage', result: false });
      } catch (e) {
        isIncognito = true;
        incognitoDetectionMethods.push({ method: 'localStorage', result: true, error: e.message });
      }
      
      // Method 3: Check if cookies are enabled but sessionStorage is not
      try {
        const cookiesEnabled = navigator.cookieEnabled;
        let sessionStorageEnabled = true;
        
        try {
          sessionStorage.setItem('incognito_test', '1');
          sessionStorage.removeItem('incognito_test');
        } catch (e) {
          sessionStorageEnabled = false;
        }
        
        if (cookiesEnabled && !sessionStorageEnabled) {
          isIncognito = true;
        }
        
        incognitoDetectionMethods.push({
          method: 'cookiesVsSessionStorage',
          result: cookiesEnabled && !sessionStorageEnabled,
          cookiesEnabled,
          sessionStorageEnabled
        });
      } catch (e) {
        console.error('[TRIAL] Error in cookies vs sessionStorage check:', e);
      }
      
      console.log('[TRIAL] Incognito detection results:', {
        isIncognito,
        methods: incognitoDetectionMethods
      });
      
      return isIncognito;
    } catch (e) {
      console.error('[TRIAL] Error checking incognito mode:', e);
      return false;
    }
  }

  /**
   * Get or create a session ID for tracking
   * @returns {string} The session ID
   */
  getOrCreateSessionId() {
    // Create a unique session identifier that persists across page refreshes but not across browsers
    const sessionId = localStorage.getItem('session_id') ||
                     sessionStorage.getItem('session_id') ||
                     document.cookie.split(';').find(c => c.trim().startsWith('session_id='))?.split('=')[1] ||
                     Math.random().toString(36).substring(2, 15) +
                     Math.random().toString(36).substring(2, 15);
    
    // Store the session ID in multiple locations
    try {
      localStorage.setItem('session_id', sessionId);
      sessionStorage.setItem('session_id', sessionId);
      document.cookie = `session_id=${sessionId}; path=/; max-age=31536000; SameSite=Strict`;
    } catch (e) {
      console.error('[TRIAL] Error storing session ID:', e);
    }
    
    return sessionId;
  }

  /**
   * Prepare headers for API requests
   * @param {string} fingerprint - Browser fingerprint
   * @param {string} sessionId - Session ID
   * @param {boolean} isIncognito - Whether the browser is in incognito mode
   * @returns {Object} Headers object
   */
  prepareHeaders(fingerprint, sessionId, isIncognito) {
    const headers = {
      'Content-Type': 'application/json',
      'credentials': 'same-origin'
    };
    
    // Only include fingerprint if it's valid
    if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 10) {
      headers['X-Browser-Fingerprint'] = fingerprint;
    }
    
    // Include session ID for tracking
    if (sessionId) {
      headers['X-Session-ID'] = sessionId;
    }
    
    // Include incognito flag if detected
    if (isIncognito) {
      headers['X-Incognito-Mode'] = 'true';
    }
    
    return headers;
  }

  /**
   * Get trial status for local development
   * @returns {Object} Trial status for local development
   */
  getLocalDevelopmentStatus() {
    // Check localStorage for a simulated trial
    const storedTrial = localStorage.getItem(this.STORAGE_KEY);
    if (storedTrial) {
      const trialData = JSON.parse(storedTrial);
      const expiresAt = new Date(trialData.expiresAt);
      const currentTime = new Date();
      
      // Calculate tests available (1 per day)
      const testsAvailable = expiresAt > currentTime ? 1 : 0;
      
      this.cachedStatus = {
        type: 'trial',
        testsAvailable,
        nextTestTime: testsAvailable === 0 ? expiresAt.toISOString() : null,
        isLocalSimulation: true
      };
      
      return this.cachedStatus;
    }
    
    // Create a new simulated trial
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 24); // 24 hour trial
    
    const trialData = {
      expiresAt: expiresAt.toISOString()
    };
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trialData));
    
    this.cachedStatus = {
      type: 'trial',
      testsAvailable: 1,
      nextTestTime: null,
      isLocalSimulation: true
    };
    
    return this.cachedStatus;
  }

  /**
   * Record a test taken in local development mode
   * @returns {boolean} True if recording was successful
   */
  recordLocalTestTaken() {
    // Set expiration to now (used up the test)
    const trialData = {
      expiresAt: new Date().toISOString(),
      lastTestDate: new Date().toISOString()
    };
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trialData));
    
    // Update cached status
    // Calculate next test time based on the last test date + 24 hours
    const nextTestTime = new Date(new Date(trialData.lastTestDate).getTime() + 24 * 60 * 60 * 1000);
    
    this.cachedStatus = {
      type: 'trial',
      testsAvailable: 0,
      nextTestTime: nextTestTime.toISOString(),
      isLocalSimulation: true
    };
    
    // Also store a flag in sessionStorage to prevent multiple tests in the same session
    sessionStorage.setItem('test_taken_today', 'true');
    
    return true;
  }
}

// Export a singleton instance
export default new TrialService();